import { useCallback, useState } from 'react';

export interface TeleprompterSettings {
  fontSize: number;
  lineWidth: number;
  scrollSpeed: number;
  musicVolume: number;
  autoScroll: boolean;
}

export interface UseTeleprompterSettingsReturn {
  settings: TeleprompterSettings;
  setFontSize: (v: number) => void;
  setLineWidth: (v: number) => void;
  setScrollSpeed: (v: number) => void;
  setMusicVolume: (v: number) => void;
  setAutoScroll: (v: boolean) => void;
  resetDefaults: () => void;
}

const STORAGE_KEY = 'sonematic-teleprompter-settings';

const DEFAULTS: TeleprompterSettings = {
  fontSize: 48,
  lineWidth: 20,
  scrollSpeed: 50,
  musicVolume: 80,
  autoScroll: true,
};

const FONT_SIZE_MIN = 24;
const FONT_SIZE_MAX = 96;
const LINE_WIDTH_MIN = 15;
const LINE_WIDTH_MAX = 40;
const SCROLL_SPEED_MIN = 10;
const SCROLL_SPEED_MAX = 100;
const MUSIC_VOLUME_MIN = 0;
const MUSIC_VOLUME_MAX = 100;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function loadSettings(): TeleprompterSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { ...DEFAULTS };
    const parsed = JSON.parse(stored) as Partial<TeleprompterSettings>;
    return {
      fontSize: clamp(
        typeof parsed.fontSize === 'number' ? parsed.fontSize : DEFAULTS.fontSize,
        FONT_SIZE_MIN,
        FONT_SIZE_MAX,
      ),
      lineWidth: clamp(
        typeof parsed.lineWidth === 'number' ? parsed.lineWidth : DEFAULTS.lineWidth,
        LINE_WIDTH_MIN,
        LINE_WIDTH_MAX,
      ),
      scrollSpeed: clamp(
        typeof parsed.scrollSpeed === 'number' ? parsed.scrollSpeed : DEFAULTS.scrollSpeed,
        SCROLL_SPEED_MIN,
        SCROLL_SPEED_MAX,
      ),
      musicVolume: clamp(
        typeof parsed.musicVolume === 'number' ? parsed.musicVolume : DEFAULTS.musicVolume,
        MUSIC_VOLUME_MIN,
        MUSIC_VOLUME_MAX,
      ),
      autoScroll: typeof parsed.autoScroll === 'boolean' ? parsed.autoScroll : DEFAULTS.autoScroll,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function saveSettings(settings: TeleprompterSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function useTeleprompterSettings(): UseTeleprompterSettingsReturn {
  const [settings, setSettings] = useState<TeleprompterSettings>(loadSettings);

  const setFontSize = useCallback((v: number) => {
    setSettings((prev) => {
      const next = { ...prev, fontSize: clamp(v, FONT_SIZE_MIN, FONT_SIZE_MAX) };
      saveSettings(next);
      return next;
    });
  }, []);

  const setLineWidth = useCallback((v: number) => {
    setSettings((prev) => {
      const next = { ...prev, lineWidth: clamp(v, LINE_WIDTH_MIN, LINE_WIDTH_MAX) };
      saveSettings(next);
      return next;
    });
  }, []);

  const setScrollSpeed = useCallback((v: number) => {
    setSettings((prev) => {
      const next = { ...prev, scrollSpeed: clamp(v, SCROLL_SPEED_MIN, SCROLL_SPEED_MAX) };
      saveSettings(next);
      return next;
    });
  }, []);

  const setMusicVolume = useCallback((v: number) => {
    setSettings((prev) => {
      const next = { ...prev, musicVolume: clamp(v, MUSIC_VOLUME_MIN, MUSIC_VOLUME_MAX) };
      saveSettings(next);
      return next;
    });
  }, []);

  const setAutoScroll = useCallback((v: boolean) => {
    setSettings((prev) => {
      const next = { ...prev, autoScroll: v };
      saveSettings(next);
      return next;
    });
  }, []);

  const resetDefaults = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSettings({ ...DEFAULTS });
  }, []);

  return {
    settings,
    setFontSize,
    setLineWidth,
    setScrollSpeed,
    setMusicVolume,
    setAutoScroll,
    resetDefaults,
  };
}
